// frontend/src/components/upload/QualityReport.jsx
import React from 'react';
import { AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react';

const QualityReport = ({ report }) => {
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          icon: AlertCircle,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          iconColor: 'text-red-600'
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          textColor: 'text-orange-800',
          iconColor: 'text-orange-600'
        };
      case 'info':
        return {
          icon: Info,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          iconColor: 'text-blue-600'
        };
      default:
        return {
          icon: CheckCircle,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          iconColor: 'text-green-600'
        };
    }
  };

  return (
    <div className="space-y-4">
      
      {/* Résumé global */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {report.data_completeness.toFixed(1)}%
          </div>
          <div className="text-sm text-gray-600">Complétude des données</div>
        </div>
        
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {report.gaps_detected.length}
          </div>
          <div className="text-sm text-gray-600">Gaps détectés</div>
        </div>
        
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {report.outliers_detected.length}
          </div>
          <div className="text-sm text-gray-600">Valeurs aberrantes</div>
        </div>
      </div>

      {/* Erreurs critiques */}
      {report.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Erreurs critiques ({report.errors.length})
          </h4>
          <ul className="space-y-2">
            {report.errors.map((error, index) => (
              <li key={index} className="text-red-700 text-sm">
                • {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Avertissements */}
      {report.warnings.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Avertissements ({report.warnings.length})
          </h4>
          <ul className="space-y-2">
            {report.warnings.map((warning, index) => (
              <li key={index} className="text-orange-700 text-sm">
                • {warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Détail des gaps */}
      {report.gaps_detected.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
            <Info className="w-5 h-5" />
            Gaps temporels détectés
          </h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {report.gaps_detected.slice(0, 5).map((gap, index) => (
              <div key={index} className="text-blue-700 text-sm bg-blue-100 rounded p-2">
                <div className="font-medium">
                  {new Date(gap.timestamp).toLocaleString('fr-FR')}
                </div>
                <div className="text-blue-600">
                  Durée: {gap.duration_minutes}min - Sévérité: {gap.severity}
                </div>
              </div>
            ))}
            {report.gaps_detected.length > 5 && (
              <div className="text-blue-600 text-sm italic">
                Et {report.gaps_detected.length - 5} autres gaps...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Outliers détectés */}
      {report.outliers_detected.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Valeurs aberrantes détectées
          </h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {report.outliers_detected.slice(0, 5).map((outlier, index) => {
              const config = getSeverityConfig(outlier.severity);
              const Icon = config.icon;
              
              return (
                <div key={index} className="text-yellow-700 text-sm bg-yellow-100 rounded p-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${config.iconColor}`} />
                    <div className="font-medium">
                      {new Date(outlier.timestamp).toLocaleString('fr-FR')}
                    </div>
                  </div>
                  <div className="text-yellow-600 ml-6">
                    Valeur: {outlier.value}kW - Plage attendue: {outlier.expected_range}
                  </div>
                </div>
              );
            })}
            {report.outliers_detected.length > 5 && (
              <div className="text-yellow-600 text-sm italic">
                Et {report.outliers_detected.length - 5} autres outliers...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recommandations */}
      {report.recommendations.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Recommandations d'amélioration
          </h4>
          <ul className="space-y-2">
            {report.recommendations.map((recommendation, index) => (
              <li key={index} className="text-green-700 text-sm">
                • {recommendation}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions automatiques disponibles */}
      {report.available_auto_fixes && report.available_auto_fixes.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-3">
            ✨ Corrections automatiques appliquées
          </h4>
          <ul className="space-y-1">
            {report.available_auto_fixes.map((fix, index) => (
              <li key={index} className="text-blue-700 text-sm">
                ✓ {fix}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default QualityReport;
